(function () {
  /* ============================================================
     Maison Thaevia — Bridal PDP JavaScript
     Extends mt-product.js behaviour with:
       · Ring-profile blueprint that updates on shape + carat change
       · Coloured stone notice toggle
       · Platinum-aware metal label formatting
     ============================================================ */

  const state = {
    shape: null,
    metal: null,
    size: null,
    carat: null,
    variantId: null,
  };

  const variants = JSON.parse(
    document.getElementById('mt-pdp-variants-json')?.textContent || '[]'
  );

  /* ---- Price display ---- */
  function updatePrice(variantId) {
    const v = variants.find((x) => x.id === variantId);
    if (!v) return;
    const priceEl = document.getElementById('mt-pdp-price');
    if (priceEl) priceEl.textContent = v.price_formatted;
  }

  /* ---- Gallery ---- */
  function initGallery() {
    const thumbs = document.querySelectorAll('.mt-pdp-thumb');
    const mainImages = document.querySelectorAll('.mt-pdp-main__img');
    const mainEl = document.querySelector('.mt-pdp-main');
    const labelEl = document.querySelector('.mt-pdp-main__label');

    if (!thumbs.length) return;

    thumbs.forEach((thumb, i) => {
      thumb.addEventListener('click', () => {
        thumbs.forEach((t) => t.classList.remove('is-active'));
        mainImages.forEach((m) => m.classList.remove('is-active'));
        thumb.classList.add('is-active');
        if (mainImages[i]) mainImages[i].classList.add('is-active');
        if (labelEl) labelEl.textContent = thumb.dataset.label || '';
      });
    });

  }

  /* ---- Metal → gallery group mapping ---- */
  function metalToGroup(metalValue) {
    if (!metalValue) return null;
    const v = metalValue.toLowerCase();
    if (v.includes('yellow'))   return 'yellow';
    if (v.includes('rose'))     return 'rose';
    if (v.includes('white'))    return 'white';
    if (v.includes('platinum')) return 'platinum';
    if (v.includes('silver'))   return 'silver';
    return null;
  }

  /* ---- Gallery metal filter ----
     Each image carries data-metal-groups="yellow" or "white platinum silver" or "all".
     "all" (the default for untagged images) means show for every metal.
     Falls back to showing all images if no tagged images match the selected group. */
  function filterGallery(group) {
    const thumbs   = [...document.querySelectorAll('.mt-pdp-thumb')];
    const mainImgs = [...document.querySelectorAll('.mt-pdp-main__img')];
    const labelEl  = document.querySelector('.mt-pdp-main__label');

    function matchesGroup(thumb) {
      const groups = (thumb.dataset.metalGroups || 'all').split(' ');
      return groups.includes('all') || groups.includes(group);
    }

    const matching = group ? thumbs.filter(matchesGroup) : thumbs;
    const visible  = matching.length > 0 ? matching : thumbs;

    thumbs.forEach((t, i) => {
      const show = visible.includes(t);
      t.hidden = !show;
      if (mainImgs[i]) mainImgs[i].hidden = !show;
    });

    thumbs.forEach((t) => t.classList.remove('is-active'));
    mainImgs.forEach((m) => m.classList.remove('is-active'));

    const first = visible[0];
    if (first) {
      first.classList.add('is-active');
      const idx = thumbs.indexOf(first);
      if (mainImgs[idx]) mainImgs[idx].classList.add('is-active');
      if (labelEl) labelEl.textContent = first.dataset.label || '';
    }
  }

  /* ---- Shape selector ----
     Shapes are now navigation links to separate products.
     The current shape is pre-marked is-active in Liquid.
     We just read the fixed value from the hidden input. */
  function initShapes() {
    const input = document.getElementById('mt-prop-shape');
    if (input) state.shape = input.value;
  }

  /* ---- Stone colour — read current from hidden input, show notice if non-white ---- */
  function initColour() {
    const input = document.getElementById('mt-prop-colour');
    const notice = document.getElementById('mt-bridal-colour-notice');
    if (input && notice) {
      notice.hidden = (input.value === 'white');
    }
  }

  /* ---- Metal chooser — tone dot → karat sub-row → full variant name ---- */
  function initMetal() {
    const toneDots   = document.querySelectorAll('.mt-pdp-dot[data-metal-tone]');
    const karatRows  = document.querySelectorAll('.mt-pdp-karat-row[data-for-tone]');
    const karatPills = document.querySelectorAll('.mt-pdp-pill[data-metal-full]');
    const metalLabel = document.getElementById('mt-metal-selected');

    function selectKarat(pill) {
      karatPills.forEach((p) => p.classList.remove('is-active'));
      pill.classList.add('is-active');
      state.metal = pill.dataset.metalFull;
      if (metalLabel) metalLabel.textContent = state.metal;
      filterGallery(metalToGroup(state.metal));
      updateCaratAvailability();
      updateSizeAvailability();
      syncVariantState();
    }

    function selectTone(dot) {
      toneDots.forEach((d) => d.classList.remove('is-active'));
      dot.classList.add('is-active');
      const tid = dot.dataset.metalTone;

      karatRows.forEach((row) => { row.hidden = row.dataset.forTone !== tid; });

      const row = document.querySelector(`.mt-pdp-karat-row[data-for-tone="${tid}"]`);
      if (row) {
        /* Gold tone — auto-select first available karat */
        const first = [...row.querySelectorAll('.mt-pdp-pill[data-metal-full]')].find((p) => !p.disabled);
        if (first) selectKarat(first);
      } else {
        /* Platinum / Silver — set directly, no sub-row */
        state.metal = dot.dataset.metalName;
        if (metalLabel) metalLabel.textContent = state.metal;
        filterGallery(metalToGroup(state.metal));
        updateCaratAvailability();
        updateSizeAvailability();
        syncVariantState();
      }
    }

    toneDots.forEach((dot) => dot.addEventListener('click', () => selectTone(dot)));
    karatPills.forEach((pill) => pill.addEventListener('click', () => {
      if (!pill.disabled) selectKarat(pill);
    }));

    /* Boot — first tone that has at least one available karat */
    const firstDot = [...toneDots].find((dot) => {
      const tid = dot.dataset.metalTone;
      const row = document.querySelector(`.mt-pdp-karat-row[data-for-tone="${tid}"]`);
      if (!row) return true; /* platinum/silver always selectable */
      return [...row.querySelectorAll('.mt-pdp-pill[data-metal-full]')].some((p) => !p.disabled);
    });
    if (firstDot) selectTone(firstDot);
  }

  /* ---- Availability helpers ---- */
  function matchesOptions(variant, values) {
    return values.filter(Boolean).every((value) => variant.options.includes(value));
  }

  /* Variant exists (regardless of stock) — used to decide whether to disable a pill */
  function hasVariant(values) {
    return variants.some((variant) => matchesOptions(variant, values));
  }

  function hasAvailableVariant(values) {
    return variants.some((variant) => matchesOptions(variant, values) && variant.available);
  }

  /* ---- Carat availability + auto-reselect (call after metal changes) ---- */
  function updateCaratAvailability() {
    const caratPills = [...document.querySelectorAll('.mt-pdp-pill[data-carat-val]')];
    caratPills.forEach((p) => {
      /* Disable only if no variant exists for this metal + carat combination */
      p.disabled = !hasVariant([state.metal, p.dataset.caratVal]);
    });

    const activeCarat = caratPills.find((p) => p.classList.contains('is-active'));
    if (activeCarat?.disabled) {
      caratPills.forEach((p) => p.classList.remove('is-active'));
      const firstAvail = caratPills.find((p) => !p.disabled);
      if (firstAvail) {
        firstAvail.classList.add('is-active');
        state.carat = firstAvail.dataset.caratVal;
        const caratInput = document.getElementById('mt-prop-carat');
        if (caratInput) caratInput.value = `${state.carat}ct`;
        const caratLabel = document.getElementById('mt-carat-selected');
        if (caratLabel) caratLabel.textContent = `${state.carat}ct`;
        updateBlueprint();
      } else {
        state.carat = null;
      }
    }
  }

  /* ---- Size availability + auto-reselect (call after metal or carat changes) ---- */
  function updateSizeAvailability() {
    const sizePills = [...document.querySelectorAll('.mt-pdp-pill[data-size]')];
    sizePills.forEach((p) => {
      /* Disable only if no variant exists for this metal + carat + size combination */
      p.disabled = !hasVariant([state.metal, state.carat, p.dataset.size]);
    });

    const activeSize = sizePills.find((p) => p.classList.contains('is-active'));
    if (activeSize?.disabled) {
      sizePills.forEach((p) => p.classList.remove('is-active'));
      const firstAvail = sizePills.find((p) => !p.disabled);
      if (firstAvail) {
        firstAvail.classList.add('is-active');
        state.size = firstAvail.dataset.size;
        const sizeInput = document.getElementById('mt-prop-size');
        if (sizeInput) sizeInput.value = state.size;
        const sizeLabel = document.getElementById('mt-size-selected');
        if (sizeLabel) sizeLabel.textContent = state.size;
      } else {
        state.size = null;
      }
    }
  }

  /* ---- Size / ring size pills ---- */
  function initSize() {
    const sizePills = document.querySelectorAll('.mt-pdp-pill[data-size]');
    const sizeLabel = document.getElementById('mt-size-selected');

    sizePills.forEach((pill) => {
      pill.addEventListener('click', () => {
        if (pill.disabled) return;
        sizePills.forEach((p) => p.classList.remove('is-active'));
        pill.classList.add('is-active');
        state.size = pill.dataset.size;
        const input = document.getElementById('mt-prop-size');
        if (input) input.value = state.size;
        if (sizeLabel) sizeLabel.textContent = state.size;

        syncVariantState();
      });
    });

    const firstEnabledSize = [...sizePills].find((p) => !p.disabled);
    if (firstEnabledSize) firstEnabledSize.click();
  }

  /* ---- Ring-profile blueprint ---- */

  /* Stone dimensions from carat weight and shape */
  function caratDims(carat, shape) {
    const ratios = {
      round: 1, princess: 0.95, cushion: 0.75, asscher: 0.78,
      oval: 0.66, pear: 0.66, emerald: 0.72, radiant: 0.72,
      marquise: 0.5,
    };
    const ratio = ratios[shape] || 0.75;
    const isSquare = shape === 'round' || shape === 'princess';
    const mmW = (isSquare ? 6.4 : 7.0) * Math.cbrt(carat);
    const mmH = mmW * ratio;
    const rx = Math.max(7, Math.min(26, mmW * 1.7));
    const ry = Math.max(5, rx * ratio);
    return { rx, ry, mm: `${mmW.toFixed(1)} × ${mmH.toFixed(1)} mm` };
  }

  function updateBlueprint() {
    const carat = parseFloat(state.carat) || 1.0;
    const shape = (state.shape || 'Round').toLowerCase();
    const c = caratDims(carat, shape);

    const stoneEl = document.getElementById('mt-blueprint-stone');
    const girdleEl = document.getElementById('mt-blueprint-girdle');
    const ticksEl = document.getElementById('mt-blueprint-ticks');
    const clawsEl = document.getElementById('mt-blueprint-claws');
    const mmEl = document.getElementById('mt-carat-mm');

    if (stoneEl) {
      stoneEl.setAttribute('rx', c.rx.toFixed(1));
      stoneEl.setAttribute('ry', c.ry.toFixed(1));
    }
    if (girdleEl) {
      girdleEl.setAttribute('x1', (65 - c.rx).toFixed(1));
      girdleEl.setAttribute('x2', (65 + c.rx).toFixed(1));
    }
    if (ticksEl) {
      const l = (65 - c.rx).toFixed(1);
      const r = (65 + c.rx).toFixed(1);
      const w = (2 * c.rx).toFixed(1);
      ticksEl.setAttribute('d', `M${l} 10 v6 M${r} 10 v6 M${l} 13 h${w}`);
    }
    if (clawsEl) {
      const lc = (65 - c.rx * 0.7).toFixed(1);
      const rc = (65 + c.rx * 0.7).toFixed(1);
      clawsEl.setAttribute('d', `M${lc} 36 l-3 6 M${rc} 36 l3 6`);
    }
    if (mmEl) mmEl.textContent = c.mm;
  }

  /* ---- Carat selector ---- */
  function initCarat() {
    const pills = document.querySelectorAll('.mt-pdp-pill[data-carat-val]');
    const input = document.getElementById('mt-prop-carat');
    const label = document.getElementById('mt-carat-selected');

    function selectCarat(pill) {
      pills.forEach((p) => p.classList.remove('is-active'));
      pill.classList.add('is-active');
      state.carat = pill.dataset.caratVal;
      if (input) input.value = `${state.carat}ct`;
      if (label) label.textContent = `${state.carat}ct`;
      updateBlueprint();
      updateSizeAvailability();
      syncVariantState();
    }

    pills.forEach((pill) => pill.addEventListener('click', () => {
      if (!pill.disabled) selectCarat(pill);
    }));

    const firstEnabled = [...pills].find((p) => !p.disabled);
    if (firstEnabled) {
      selectCarat(firstEnabled);
    } else {
      state.carat = null;
    }
  }

  /* ---- Variant matching — Metal × Ct size × Ring size — strict only ---- */
  function matchVariantStrict() {
    if (!state.metal || !state.carat || !state.size) return null;

    return variants.find((variant) =>
      variant.options.includes(state.metal) &&
      variant.options.includes(state.carat) &&
      variant.options.includes(state.size)
    ) || null;
  }

  /* ---- Centralized variant sync ---- */
  function syncVariantState() {
    const variant = matchVariantStrict();
    const addBtn = document.querySelector('.mt-pdp-add-btn');
    const variantInput = document.getElementById('mt-pdp-variant-id');
    const notice = document.getElementById('mt-pdp-config-notice');

    function setNotice(msg) {
      if (!notice) return;
      notice.textContent = msg;
      notice.hidden = !msg;
    }

    if (!variant) {
      /* No exact variant exists — cannot place an order; route to enquiry */
      if (variantInput) variantInput.value = '';
      if (addBtn) addBtn.disabled = true;
      setNotice('This configuration is available by enquiry.');
      return;
    }

    /* Exact variant exists — set it and keep Add to Bag enabled even if out of stock.
       Shopify must have "Continue selling when out of stock" enabled on the product
       for unavailable variants to be added to cart. */
    if (variantInput) variantInput.value = variant.id;
    updatePrice(variant.id);
    if (addBtn) addBtn.disabled = false;
    setNotice(variant.available ? '' : 'Made to order · availability confirmed by the Maison.');
  }

  /* ---- Wishlist (UI only) ---- */
  function initWishlist() {
    const btn = document.querySelector('.mt-pdp-wishlist-btn');
    if (!btn) return;
    btn.addEventListener('click', () => {
      btn.classList.toggle('is-saved');
      btn.setAttribute(
        'aria-label',
        btn.classList.contains('is-saved') ? 'Remove from wishlist' : 'Save to wishlist'
      );
    });
  }

  /* ---- Add to Bag ---- */
  function initAddToBag() {
    const form = document.getElementById('mt-pdp-form');
    if (!form) return;

    form.addEventListener('submit', (e) => {
      setHiddenInput(form, 'properties[Shape]', state.shape || '');
      /* Carat weight is already encoded in the variant title (Ct size option) — not submitted as a line-item property */
      const colourInput = document.getElementById('mt-prop-colour');
      if (colourInput?.value && colourInput.value !== 'white') {
        setHiddenInput(form, 'properties[Stone Colour]', colourInput.value);
      }
    });
  }

  function setHiddenInput(form, name, value) {
    let el = form.querySelector(`input[name="${name}"]`);
    if (!el) {
      el = document.createElement('input');
      el.type = 'hidden';
      el.name = name;
      form.appendChild(el);
    }
    el.value = value;
  }

  /* ---- Engraving character counter ---- */
  function initEngraving() {
    const input = document.getElementById('mt-engraving-input');
    const counter = document.getElementById('mt-engraving-count');
    if (!input || !counter) return;

    const max = parseInt(input.getAttribute('maxlength') || '30', 10);

    input.addEventListener('input', () => {
      const len = input.value.length;
      counter.textContent = `${len} / ${max}`;
      counter.style.opacity = len > max * 0.85 ? '1' : '';
      counter.style.color = len >= max ? 'var(--mt-antique-bronze)' : '';
    });
  }

  /* ---- Accordion (close siblings) ---- */
  function initAccordion() {
    document.querySelectorAll('.mt-pdp-accordion details').forEach((detail) => {
      detail.addEventListener('toggle', () => {
        if (!detail.open) return;
        document.querySelectorAll('.mt-pdp-accordion details').forEach((other) => {
          if (other !== detail) other.removeAttribute('open');
        });
      });
    });
  }

  /* ---- Boot ---- */
  function init() {
    initGallery();
    initShapes();
    initColour();
    initMetal();
    initSize();
    initCarat();
    initEngraving();
    initWishlist();
    initAddToBag();
    initAccordion();

    syncVariantState();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (e) => {
    if (e.target.querySelector('#mt-pdp-form')) init();
  });
})();
